import { BizAoiAttributeAccessCodeInput } from '../../aoi-platform/graphql/mutations/upsertAoiAttribute';
import { AccessCodeDraft } from '../types';

/**
 * AOI 门禁码的草稿 ↔ 入参转换 (AOI 级门禁, 落 biz_aoi_attribute.access_codes)。
 *
 * <p>抽出来独立成模块的原因: 抽屉的 dirty 比对直接拿 {@link toAccessCodeInputs} 的结果做
 * JSON.stringify 对比, 所以它必须是纯函数且字段顺序稳定 —— 这条约束值得单测盯着。
 */

/** detail 返回的门禁码 → 抽屉草稿 (uid 不留: 服务端每次按 类型 + 码值 重算)。 */
export function toAccessCodeDrafts(
    codes: {
        id?: string | null;
        codeType: string | null;
        codeValue: string | null;
        available: string | null;
        remark: string | null;
    }[] | undefined | null,
): AccessCodeDraft[] {
    return (codes ?? []).map((c) => ({
        id: c.id ?? null,
        codeType: c.codeType ?? '',
        codeValue: c.codeValue ?? '',
        available: c.available ?? 'PENDING_VERIFICATION',
        remark: c.remark ?? null,
    }));
}

/**
 * 抽屉草稿 → 提交入参: trim + 丢掉 类型 / 码值 任一为空的行
 * (用户点了"添加门禁码"但没填完就保存, 不该把空行写进 JSON)。
 */
export function toAccessCodeInputs(codes: AccessCodeDraft[]): BizAoiAttributeAccessCodeInput[] {
    return codes
        .map((c) => ({
            id: c.id ?? null,
            codeType: c.codeType.trim(),
            codeValue: c.codeValue.trim(),
            available: c.available || 'PENDING_VERIFICATION',
            remark: c.remark?.trim() || null,
        }))
        .filter((c) => c.codeType !== '' && c.codeValue !== '');
}

/**
 * 门禁码重复校验: 同 类型 + 码值 视为同一条门禁 (服务端 uid 也会算成同一个), 提交前先拦住。
 *
 * @returns 重复时返回中文提示文案; 无重复返回 null
 */
export function findDuplicateAccessCode(codes: BizAoiAttributeAccessCodeInput[]): string | null {
    const seen = new Set<string>();
    for (const c of codes) {
        const key = `${c.codeType}:${c.codeValue}`;
        if (seen.has(key)) {
            return `门禁码重复: ${c.codeType} / ${c.codeValue}`;
        }
        seen.add(key);
    }
    return null;
}
